import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { getPriceDifferenceDisplay } from '@/core/utils/dealDisplay';
import { descriptorDimensions } from '@/core/utils/rapidstor';

import store from '@/store';
import {
    ReservationData,
    selectUpsell,
    setReservationData,
    setUnitData,
    setUpsell,
} from '@/store/bookingSlice';

import Button from '@/core/components/elements/button';

import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
} from '@/features/rapidstor/animations';
import { getEffectiveDealRate } from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';
import { descriptorTitle, formatDealCustomText } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { DescriptorOptions } from '@/features/rapidstor/UnitOptions';

import baseClasses from '../styles.module.scss';

interface Props {
    descriptor: Descriptor;
    unitType: UnitType;
    desktop?: boolean;
    loading: boolean;
}

export default function UpsellCard({
    descriptor,
    unitType,
    desktop,
    loading,
}: Props) {
    const location = useLoaderData() as Location;
    const upSell = useSelector(selectUpsell);
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { unitData, reservationData } = useContext(JourneyContext);

    const { t } = useTranslation();

    const availableDeal = useMemo(() => {
        if (!unitType.deals || unitType.deals.length === 0) {
            return null;
        }
        // Try to match the current deal
        if (reservationData?.dealObject) {
            const matchingDeal = unitType.deals.find((deal) => deal._id === reservationData.dealObject?._id);
            if (matchingDeal) return matchingDeal;
        }
        // If hideStandardPrices is enabled, fall back to first available deal
        if (location.rapidstor.hideStandardPrices) {
            return unitType.deals[0];
        }
        return null;
    }, [unitType.deals, reservationData, location.rapidstor.hideStandardPrices]);

    const actualPriceDifference = useMemo(() => {
        const currentDeal = reservationData?.dealObject;
        const currentRate = currentDeal
            ? getEffectiveDealRate(currentDeal, reservationData.unitType.rates.rate)
            : (reservationData?.unitType.rates.rate ?? 0);
        const upsellRate = availableDeal
            ? getEffectiveDealRate(availableDeal, unitType.rates.rate)
            : unitType.rates.rate;
        return upsellRate - currentRate;
    }, [availableDeal, unitType.rates.rate, reservationData]);

    const priceDifferenceDisplay = useMemo(() => {
        if (!actualPriceDifference || actualPriceDifference === 0) {
            return 'No Price Change';
        }
        const currentDeal = reservationData?.dealObject;
        const sourceRate = currentDeal
            ? getEffectiveDealRate(currentDeal, reservationData?.unitType.rates.rate ?? 0)
            : (reservationData?.unitType.rates.rate ?? 0);
        const targetRate = availableDeal
            ? getEffectiveDealRate(availableDeal, unitType.rates.rate)
            : unitType.rates.rate;
        return getPriceDifferenceDisplay(
            sourceRate,
            targetRate,
            currentDeal ?? undefined,
            availableDeal ?? undefined,
            location,
        );
    }, [actualPriceDifference, reservationData, availableDeal, unitType.rates.rate, location]);

    const selectUpsellCard = () => {
        if (!unitData) {
            return;
        }

        const useDescriptorOptions = DescriptorOptions(descriptor, unitData.descriptors, location);
        const upSellDescriptor = useDescriptorOptions.nextSizeUp(
            unitData.descriptors,
            descriptor.area,
        );

        store.dispatch(
            setUnitData({
                location,
                unitType,
                descriptor,
                descriptors: unitData.descriptors,
            }),
        );

        if (upSellDescriptor) {
            store.dispatch(setUpsell(upSellDescriptor));
        }

        const dealToUse = availableDeal ?? null;

        const reservationStoreData: ReservationData = {
            ...reservationData,
            unitType,
            descriptor,
            deal: dealToUse?._id || '',
            dealObject: dealToUse || undefined,
            coupon: null,
        };

        store.dispatch(setReservationData(reservationStoreData));
        firebaseLogEvent('select_upsell', {});
    };

    const classNames = [
        baseClasses.card,
        desktop ? baseClasses.desktop : baseClasses.mobile,
        baseClasses.upsell,
    ].join(' ');

    if (!upSell) {
        return null;
    }

    return (
        <motion.div
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                duration: ANIMATION_SPEED,
                delay: ANIMATION_DELAY * 2,
            }}
            className={`${classNames}`}
        >
            <div className={`${baseClasses.card_body}`}>
                {availableDeal && (
                    <>
                        <div
                            className={`${baseClasses.deal_banner}`}
                            style={{
                                marginBottom: '8px', marginTop: '-20px', fontWeight: '700', textAlign: 'center', fontSize: '16px',
                            }}
                        >
                            <span>{availableDeal.title}</span>
                        </div>
                        {(availableDeal.customFormat?.primary || availableDeal.customFormat?.secondary)
                            && (availableDeal.rates.discountedRate ?? 0) > 0 && (
                            <div style={{
                                marginBottom: '16px', textAlign: 'center', fontSize: '13px', lineHeight: '1.4',
                            }}
                            >
                                {availableDeal.customFormat.primary && (
                                    <div>
                                        {formatDealCustomText(
                                            availableDeal,
                                            location,
                                            availableDeal.customFormat.primary,
                                        )}
                                    </div>
                                )}
                                {availableDeal.customFormat.secondary && (
                                    <div>
                                        {formatDealCustomText(
                                            availableDeal,
                                            location,
                                            availableDeal.customFormat.secondary,
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
                <div className={`${baseClasses.upsell_header}`}>
                    <img
                        alt="Upsell"
                        src={upSell.picture || location.skin.defaultUnitPicture}
                        className={`${baseClasses.mobile}`}
                    />
                    <div className={`${baseClasses.left}`}>
                        <span>{descriptorTitle(upSell, location)[1]}</span>
                        <label>
                            {descriptorDimensions(upSell.unitTypes[0], location, { parens: true, showDimensionType: true })}
                        </label>
                    </div>
                    <div className={`${baseClasses.right}`}>
                        <span>{priceDifferenceDisplay}</span>

                        {(() => {
                            if (!actualPriceDifference || actualPriceDifference === 0) {
                                return null;
                            }

                            if (actualPriceDifference > 0) {
                                return (
                                    <label>
                                        {t('unit_card:extra')}
                                        {' '}
                                        {formatPriceLabel(
                                            formatCurrency(actualPriceDifference ?? 0, location),
                                            location,
                                            true,
                                        )}
                                    </label>
                                );
                            }
                            return (
                                <label>
                                    {t('unit_card:save')}
                                    {' '}
                                    {formatPriceLabel(
                                        formatCurrency(Math.abs(actualPriceDifference ?? 0), location),
                                        location,
                                        true,
                                    )}
                                </label>
                            );
                        })()}
                    </div>
                </div>
                <div
                    className={`${baseClasses.upsell_body} ${baseClasses.desktop}`}
                >
                    <img className="" src={upSell.picture || location.skin.defaultUnitPicture} alt="Upsell" />
                </div>

                <Button
                    disabled={loading}
                    onclick={() => {
                        selectUpsellCard();
                    }}
                    type="primary"
                    size="medium"
                    testId="pay-now"
                >
                    <label>{t('customise_booking:upgrade')}</label>
                </Button>
            </div>
        </motion.div>
    );
}
